import { RobotApi } from 'api/RobotApi'
import store from 'store/store'
import { Point } from 'geojson'
import { useLaserscanWebSocket } from 'hooks/useLaserscanWebSocket'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ROBOT_LOCALIZATION_COMMAND } from 'utils/constants'
import { useSuppressNotificationSandwich } from 'hooks/useSuppressNotificationSandwich'
import { NOTIFICATION_SANDWICH_SUPPRESS } from 'utils/notificationSandwichGate'
import { getYawFromQuaternion } from 'utils/getYawFromQuaternion'
import { LOCALIZATION_MODE_GNSS } from 'utils/setPoseUi'

type Orientation = { x: number; y: number; z: number; w: number }

function isValidGeoCoordinate(lng: number, lat: number): boolean {
  return Number.isFinite(lng) && Number.isFinite(lat) && lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90
}

/** Prefer live WS pose; fall back to REST lastPosition from page load. */
export function resolveSetPoseSeed(params: {
  livePosition?: [number, number] | null
  lastPosition?: Point
  liveOrientation?: Orientation | null
  lastOrientation?: Orientation | null
}): { position?: Point; yaw: number } {
  let coordinates: [number, number] | undefined

  if (params.livePosition?.length === 2) {
    const [lng, lat] = params.livePosition
    if (isValidGeoCoordinate(lng, lat)) {
      coordinates = [lng, lat]
    }
  }

  if (!coordinates && params.lastPosition?.coordinates?.length === 2) {
    const [lng, lat] = params.lastPosition.coordinates
    if (isValidGeoCoordinate(lng, lat)) {
      coordinates = [lng, lat]
    }
  }

  const orientation = params.liveOrientation ?? params.lastOrientation ?? null

  return {
    position: coordinates ? { type: 'Point', coordinates } : undefined,
    yaw: orientation ? getYawFromQuaternion(orientation) : 0,
  }
}

export function useRobotSetPoseLocalization(params: {
  robotId?: number
  robotSerialNumber?: string
  localizationMode?: number
  livePosition?: [number, number] | null
  lastPosition?: Point
  liveOrientation?: Orientation | null
  lastOrientation?: Orientation | null
}) {
  const { robotId, robotSerialNumber, localizationMode, livePosition, lastPosition, liveOrientation, lastOrientation } =
    params

  const [setPoseMode, setSetPoseMode] = useState(false)
  const [setPosePosition, setSetPosePosition] = useState<Point | undefined>(undefined)
  const [setPoseYaw, setSetPoseYaw] = useState<number | undefined>(undefined)
  const [laserscanActive, setLaserscanActive] = useState(false)
  const prevSetPoseModeRef = useRef(false)
  useSuppressNotificationSandwich(setPoseMode, NOTIFICATION_SANDWICH_SUPPRESS.SET_POSE)

  const { scan: laserscanData } = useLaserscanWebSocket({
    enabled: laserscanActive,
    robotSerialNumber,
    onTimeout: useCallback(() => {
      setLaserscanActive(false)
      setSetPoseMode(false)
    }, []),
  })

  useEffect(() => {
    const wasActive = prevSetPoseModeRef.current
    prevSetPoseModeRef.current = setPoseMode

    // Seed editable pose only when entering set-pose mode (not on every telemetry tick).
    // Read the store here so the page does not have to re-render on every live pose update.
    if (!setPoseMode || wasActive) return

    const robotState = store.getState().robot
    const seed = resolveSetPoseSeed({
      livePosition: robotState.telemetry?.livePosition ?? livePosition,
      lastPosition: robotState.robotInfo?.lastPosition ?? lastPosition,
      liveOrientation: robotState.telemetry?.quaternion ?? liveOrientation,
      lastOrientation: robotState.robotInfo?.lastOrientation ?? lastOrientation,
    })

    if (seed.position) {
      setSetPosePosition(seed.position)
    }
    setSetPoseYaw(seed.yaw)
    // Pose is read from the store when set-pose mode is entered, not on each telemetry tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPoseMode])

  useEffect(() => {
    if (localizationMode === LOCALIZATION_MODE_GNSS) {
      if (laserscanActive && robotId) {
        RobotApi.execLocalizationCommand(robotId, ROBOT_LOCALIZATION_COMMAND.DISABLE_LASERSCAN).catch(err => {
          console.error('Failed to disable laserscan on GNSS switch:', err)
        })
        setLaserscanActive(false)
      }
      return
    }

    if (setPoseMode && !laserscanActive) {
      setSetPoseMode(false)
    }
  }, [localizationMode, robotId, laserscanActive, setPoseMode])

  const toggleLaserscan = useCallback(async () => {
    if (!robotId) return

    const newActive = !laserscanActive
    try {
      await RobotApi.execLocalizationCommand(
        robotId,
        newActive ? ROBOT_LOCALIZATION_COMMAND.ENABLE_LASERSCAN : ROBOT_LOCALIZATION_COMMAND.DISABLE_LASERSCAN
      )
      setLaserscanActive(newActive)
      if (!newActive) {
        setSetPoseMode(false)
      }
    } catch (err) {
      console.error('Failed to toggle laserscan:', err)
    }
  }, [robotId, laserscanActive])

  const handleConfirmSetPose = useCallback(async () => {
    if (!robotId || !setPosePosition || setPoseYaw === undefined) return

    const [lon, lat] = setPosePosition.coordinates
    try {
      await RobotApi.execLocalizationCommand(robotId, ROBOT_LOCALIZATION_COMMAND.SET_POSE, {
        // Mapbox bearing (0=North, CW+) → ROS yaw (0=East, CCW+) in radians
        pose: { lon, lat, yaw: (90 - setPoseYaw) * (Math.PI / 180) },
      })
      setSetPoseMode(false)
    } catch (err) {
      console.error('Failed to set pose:', err)
    }
  }, [robotId, setPosePosition, setPoseYaw])

  return {
    setPoseMode,
    setSetPoseMode,
    setPosePosition,
    setSetPosePosition,
    setPoseYaw,
    setSetPoseYaw,
    laserscanActive,
    laserscanData,
    toggleLaserscan,
    handleConfirmSetPose,
  }
}
